import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle, XCircle, DollarSign, Gift, TrendingUp, Calendar, Loader2, AlertCircle, Download, LogOut, Filter, Trash2, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GiftCardAdmin = () => {
  const navigate = useNavigate();
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemService, setRedeemService] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({ total_sold: 0, total_revenue: 0, active_cards: 0, total_redeemed: 0 });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [deletingCardCode, setDeletingCardCode] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const adminEmail = localStorage.getItem('admin_email');

  // Custom gift card creation
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customRecipientName, setCustomRecipientName] = useState('');
  const [customRecipientEmail, setCustomRecipientEmail] = useState('');
  const [customServices, setCustomServices] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  const [emailPdf, setEmailPdf] = useState(false);
  const [lastCreatedCardCode, setLastCreatedCardCode] = useState(null);

  useEffect(() => {
    loadStats();
    loadTransactions();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      const response = await axios.get(`${API_URL}/api/gift-cards/admin/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift-cards/admin/recent-transactions?limit=20`);
      setRecentTransactions(response.data.transactions || []);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchCode.trim()) {
      setError('Please enter a gift card code');
      return;
    }
    try {
      setIsSearching(true);
      setError('');
      setSuccess('');
      setSearchResult(null);
      const response = await axios.get(`${API_URL}/api/gift-cards/search/${searchCode.trim().toUpperCase()}`);
      if (response.data.found) {
        setSearchResult(response.data.gift_card);
      } else {
        setError('Gift card not found. Please check the code and try again.');
      }
    } catch (err) {
      setError('Failed to search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemAmount || parseFloat(redeemAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (parseFloat(redeemAmount) > searchResult.balance) {
      setError(`Insufficient balance. Available: $${searchResult.balance}`);
      return;
    }
    try {
      setIsRedeeming(true);
      setError('');
      setSuccess('');
      const response = await axios.post(`${API_URL}/api/gift-cards/redeem/${searchCode.trim().toUpperCase()}`, {
        amount: parseFloat(redeemAmount),
        service: redeemService || 'Service Redemption'
      });
      setSearchResult({ ...searchResult, balance: response.data.new_balance, status: response.data.status });
      setSuccess(`Successfully redeemed $${redeemAmount}. New balance: $${response.data.new_balance}`);
      setRedeemAmount('');
      setRedeemService('');
      loadStats();
      loadTransactions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Redemption failed. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleMarkAsUsed = async () => {
    if (!window.confirm('Mark this service gift card as used? This cannot be undone.')) {
      return;
    }

    try {
      setIsRedeeming(true);
      setError('');
      setSuccess('');

      // Call the mark-as-used endpoint for service cards
      const response = await axios.post(`${API_URL}/api/gift-cards/mark-as-used/${searchCode.trim().toUpperCase()}`);

      setSearchResult({ 
        ...searchResult, 
        status: response.data.status 
      });
      setSuccess('✅ Service gift card marked as used successfully!');
      
      loadStats();
      loadTransactions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to mark as used. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleClearAllGiftCards = async () => {
    if (!showClearConfirm) {
      setShowClearConfirm(true);
      return;
    }
    
    try {
      setIsDeleting(true);
      
      const response = await axios.delete(`${API_URL}/api/gift-cards/admin/clear-all`);
      
      setSuccess(`Successfully deleted ${response.data.deleted_count} gift cards!`);
      setShowClearConfirm(false);
      
      // Refresh data
      loadStats();
      loadTransactions();
    } catch (error) {
      console.error('Error clearing gift cards:', error);
      setError('Failed to clear gift cards: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSingleCard = async (cardCode) => {
    if (!window.confirm(`Are you sure you want to delete gift card ${cardCode}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingCardCode(cardCode);
      setError('');
      setSuccess('');
      
      const response = await axios.delete(`${API_URL}/api/gift-cards/admin/${cardCode}`);
      
      setSuccess(`Successfully deleted gift card ${cardCode}`);
      
      // Refresh data
      loadStats();
      loadTransactions();
      
      // Clear search result if it was the deleted card
      if (searchResult && searchResult.code === cardCode) {
        setSearchResult(null);
        setSearchCode('');
      }
    } catch (error) {
      console.error('Error deleting gift card:', error);
      setError('Failed to delete gift card: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingCardCode(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const handleCreateCustom = async () => {
    if (!customRecipientName || !customRecipientEmail || !customServices) {
      setError('Please fill in recipient name, email, and services');
      return;
    }

    try {
      setIsCreatingCustom(true);
      setError('');
      setSuccess('');

      const response = await axios.post(`${API_URL}/api/gift-cards/admin/create-custom`, {
        recipient_name: customRecipientName,
        recipient_email: customRecipientEmail,
        services: customServices,
        message: customMessage,
        amount: customAmount ? parseFloat(customAmount) : 0,
        email_pdf: emailPdf
      });

      const cardCode = response.data.code;
      setLastCreatedCardCode(cardCode);
      setSuccess(`Custom gift card ${cardCode} created and sent to ${customRecipientEmail}!`);
      setCustomRecipientName('');
      setCustomRecipientEmail('');
      setCustomServices('');
      setCustomMessage('');
      setCustomAmount('');
      setEmailPdf(false);
      setShowCustomForm(false);
      
      loadStats();
      loadTransactions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create custom gift card');
    } finally {
      setIsCreatingCustom(false);
    }
  };

  const handleDownloadPdf = (cardCode) => {
    // Open PDF download in new window
    window.open(`${API_URL}/api/gift-cards/admin/${cardCode}/pdf`, '_blank');
  };

  const exportToCSV = () => {
    const csvData = recentTransactions.map(t => ({
      Code: t.code,
      Type: t.type,
      Amount: t.amount,
      Description: t.description,
      Date: formatDate(t.date)
    }));
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [headers.join(','), ...csvData.map(row => headers.map(h => row[h]).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gift-cards-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const filteredTransactions = recentTransactions.filter(t => {
    if (statusFilter !== 'all' && t.type !== statusFilter) return false;
    if (dateFilter === 'today' && new Date().toDateString() !== new Date(t.date).toDateString()) return false;
    if (dateFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      if (new Date(t.date) < weekAgo) return false;
    }
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', position: 'relative' }}>
      <div className="stars-container-gift" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        {[...Array(50)].map((_, i) => (
          <div key={i} className="star" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>
      
      <div style={{ background: 'rgba(26,26,46,0.95)', backdropFilter: 'blur(10px)', borderBottom: '2px solid var(--mystic-gold)', padding: '1.5rem 2rem', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Gift className="w-8 h-8" style={{ color: 'var(--mystic-gold)' }} />
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Admin Portal</h1>
              <p style={{ fontSize: '0.85rem', color: 'rgba(212,175,55,0.8)', margin: 0 }}>Gift Card Management</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ textAlign: 'right', marginRight: '1rem' }}>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>Logged in as</p>
              <p style={{ fontSize: '0.9rem', color: 'var(--mystic-gold)', margin: 0, fontWeight: '600' }}>{adminEmail}</p>
            </div>
            <Button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(212,175,55,0.3)', color: 'white' }}>Home</Button>
            <Button onClick={handleLogout} style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)', border: '2px solid #dc2626', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <LogOut className="w-4 h-4" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 1, padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Stats Section */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <TrendingUp style={{ color: 'var(--mystic-gold)' }} /> Dashboard Overview
            </h2>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button onClick={exportToCSV} disabled={recentTransactions.length === 0} style={{ background: 'linear-gradient(135deg, var(--mystic-gold), #b8860b)', color: '#1a1a1a', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Download className="w-4 h-4" /> Export CSV
              </Button>
              <Button 
                onClick={handleClearAllGiftCards}
                disabled={isDeleting || stats.total_sold === 0}
                style={{ 
                  background: showClearConfirm ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'rgba(239,68,68,0.2)', 
                  border: '2px solid #ef4444',
                  color: showClearConfirm ? 'white' : '#ef4444', 
                  fontWeight: 'bold', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem' 
                }}
              >
                {isDeleting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
                ) : showClearConfirm ? (
                  <><AlertCircle className="w-4 h-4" /> Click Again to Confirm</>
                ) : (
                  <><Trash2 className="w-4 h-4" /> Clear All Gift Cards</>
                )}
              </Button>
            </div>
          </div>
          {isLoadingStats ? (
            <div style={{ textAlign: 'center', padding: '3rem', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ margin: '0 auto', color: 'var(--mystic-gold)' }} />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {[
                { label: 'Total Revenue', value: `$${stats.total_revenue.toFixed(2)}`, icon: DollarSign, gradient: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.3)', iconColor: 'rgba(16,185,129,0.6)' },
                { label: 'Cards Sold', value: stats.total_sold, icon: Gift, gradient: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.3)', iconColor: 'rgba(139,92,246,0.6)' },
                { label: 'Active Cards', value: stats.active_cards, icon: CheckCircle, gradient: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.3)', iconColor: 'rgba(59,130,246,0.6)' },
                { label: 'Total Redeemed', value: `$${stats.total_redeemed.toFixed(2)}`, icon: TrendingUp, gradient: 'rgba(251,146,60,0.1)', border: 'rgba(251,146,60,0.3)', iconColor: 'rgba(251,146,60,0.6)' }
              ].map((stat, i) => (
                <Card key={i} style={{ background: `linear-gradient(135deg, ${stat.gradient}, ${stat.gradient})`, border: `2px solid ${stat.border}`, borderRadius: '16px', boxShadow: `0 10px 30px ${stat.border}` }}>
                  <CardContent style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>{stat.label}</p>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--mystic-gold)', margin: 0 }}>{stat.value}</p>
                    </div>
                    <stat.icon style={{ width: '3rem', height: '3rem', color: stat.iconColor }} />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Create Custom Gift Card Section */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Gift style={{ color: 'var(--mystic-gold)' }} /> Create Custom Gift Card
            </h2>
            <Button
              onClick={() => setShowCustomForm(!showCustomForm)}
              style={{
                background: showCustomForm ? 'var(--mystic-gold)' : 'linear-gradient(135deg, var(--mystic-purple), #6b46c1)',
                color: showCustomForm ? '#1a1a1a' : 'white',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {showCustomForm ? '✕ Close' : '+ Create Custom Card'}
            </Button>
          </div>

          {showCustomForm && (
            <Card style={{ background: 'rgba(255,255,255,0.98)', borderRadius: '16px', border: '2px solid var(--mystic-gold)', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
              <CardContent style={{ padding: '2rem' }}>
                <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
                  Create custom service gift cards for complimentary treatments, promotions, or special packages.
                </p>

                {error && <div style={{ padding: '1rem', marginBottom: '1rem', background: '#fee2e2', border: '2px solid #ef4444', borderRadius: '8px', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><AlertCircle className="w-5 h-5" />{error}</div>}
                {success && <div style={{ padding: '1rem', marginBottom: '1rem', background: '#d1fae5', border: '2px solid #10b981', borderRadius: '8px', color: '#065f46', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><CheckCircle className="w-5 h-5" />{success}</div>}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div>
                    <Label htmlFor="customRecipientName" style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--mystic-purple)', marginBottom: '0.5rem', display: 'block' }}>Recipient Name *</Label>
                    <Input id="customRecipientName" placeholder="Client name" value={customRecipientName} onChange={(e) => setCustomRecipientName(e.target.value)} style={{ fontSize: '1rem', padding: '0.75rem' }} />
                  </div>
                  <div>
                    <Label htmlFor="customRecipientEmail" style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--mystic-purple)', marginBottom: '0.5rem', display: 'block' }}>Recipient Email *</Label>
                    <Input id="customRecipientEmail" type="email" placeholder="client@example.com" value={customRecipientEmail} onChange={(e) => setCustomRecipientEmail(e.target.value)} style={{ fontSize: '1rem', padding: '0.75rem' }} />
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <Label htmlFor="customServices" style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--mystic-purple)', marginBottom: '0.5rem', display: 'block' }}>Services Included *</Label>
                  <textarea
                    id="customServices"
                    placeholder="Example:\n- Full Balayage Session\n- Haircut & Style\n- Deep Conditioning Treatment\n\nOr describe the package..."
                    value={customServices}
                    onChange={(e) => setCustomServices(e.target.value)}
                    rows={5}
                    style={{ width: '100%', fontSize: '1rem', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div>
                    <Label htmlFor="customAmount" style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--mystic-purple)', marginBottom: '0.5rem', display: 'block' }}>Dollar Value (Optional)</Label>
                    <Input id="customAmount" type="number" min="0" step="0.01" placeholder="Leave empty for service-only card" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} style={{ fontSize: '1rem', padding: '0.75rem' }} />
                    <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>Add a dollar value or leave empty for service-only cards</p>
                  </div>
                  <div>
                    <Label htmlFor="customMessage" style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--mystic-purple)', marginBottom: '0.5rem', display: 'block' }}>Personal Message (Optional)</Label>
                    <textarea
                      id="customMessage"
                      placeholder="Thank you for being a valued client!"
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows={3}
                      style={{ width: '100%', fontSize: '1rem', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontFamily: 'inherit', resize: 'vertical' }}
                    />
                  </div>
                </div>

                {/* Email PDF Option */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(139,92,246,0.05)', borderRadius: '8px', border: '1px solid rgba(139,92,246,0.2)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={emailPdf}
                      onChange={(e) => setEmailPdf(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--mystic-purple)' }}>
                        📧 Email PDF attachment to recipient
                      </span>
                      <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.25rem 0 0 0' }}>
                        Include a beautifully designed printable PDF of the gift card in the email
                      </p>
                    </div>
                  </label>
                </div>

                {/* Show Download PDF button after successful creation */}
                {success && lastCreatedCardCode && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(212,175,55,0.1)', borderRadius: '8px', border: '2px solid var(--mystic-gold)', textAlign: 'center' }}>
                    <p style={{ fontSize: '0.95rem', color: 'var(--mystic-purple)', marginBottom: '0.75rem', fontWeight: '600' }}>
                      ✨ Gift Card Created Successfully!
                    </p>
                    <Button
                      onClick={() => handleDownloadPdf(lastCreatedCardCode)}
                      style={{
                        background: 'linear-gradient(135deg, var(--mystic-gold), #b8860b)',
                        color: '#1a1a1a',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem'
                      }}
                    >
                      <FileDown className="w-5 h-5" /> Download PDF
                    </Button>
                  </div>
                )}

                <Button
                  onClick={handleCreateCustom}
                  disabled={isCreatingCustom || !customRecipientName || !customRecipientEmail || !customServices}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {isCreatingCustom ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Creating & Sending...</>
                  ) : (
                    <><Gift className="w-5 h-5" /> Create & Send Gift Card</>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Search Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <Search style={{ color: 'var(--mystic-gold)' }} /> Look Up Gift Card
          </h2>
          <Card style={{ background: 'rgba(255,255,255,0.98)', borderRadius: '16px', border: '2px solid var(--mystic-gold)', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
            <CardContent style={{ padding: '2rem' }}>
              {error && <div style={{ padding: '1rem', marginBottom: '1rem', background: '#fee2e2', border: '2px solid #ef4444', borderRadius: '8px', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><AlertCircle className="w-5 h-5" />{error}</div>}
              {success && <div style={{ padding: '1rem', marginBottom: '1rem', background: '#d1fae5', border: '2px solid #10b981', borderRadius: '8px', color: '#065f46', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><CheckCircle className="w-5 h-5" />{success}</div>}
              
              <div style={{ marginBottom: '1.5rem' }}>
                <Label htmlFor="searchCode" style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--mystic-purple)', marginBottom: '0.5rem', display: 'block' }}>Gift Card Code</Label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Input id="searchCode" placeholder="MG-XXXXXXXXX" value={searchCode} onChange={(e) => setSearchCode(e.target.value.toUpperCase())} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} style={{ flex: 1, fontSize: '1rem', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px' }} />
                  <Button onClick={handleSearch} disabled={isSearching || !searchCode.trim()} style={{ minWidth: '140px', fontSize: '1rem', padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, var(--mystic-purple), #6b46c1)', color: 'white', fontWeight: 'bold' }}>
                    {isSearching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching...</> : <><Search className="w-4 h-4 mr-2" /> Search</>}
                  </Button>
                </div>
              </div>

              {searchResult && (
                <div style={{ marginTop: '2rem', padding: '2rem', background: 'linear-gradient(135deg, rgba(139,92,246,0.05), rgba(212,175,55,0.05))', borderRadius: '12px', border: '2px solid var(--mystic-gold)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.3rem', color: 'var(--mystic-purple)', margin: 0 }}>Gift Card Details</h3>
                    <span style={{ padding: '0.5rem 1rem', background: searchResult.status === 'active' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', color: searchResult.status === 'active' ? '#059669' : '#dc2626', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {searchResult.status === 'active' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {searchResult.status}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontWeight: '600', color: '#666' }}>Code:</span>
                      <span style={{ fontFamily: 'monospace', color: 'var(--mystic-purple)', fontSize: '1.1rem', fontWeight: 'bold' }}>{searchResult.code}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontWeight: '600', color: '#666' }}>Original Amount:</span>
                      <span style={{ color: 'var(--mystic-purple)', fontWeight: '600' }}>${searchResult.amount}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'linear-gradient(135deg, rgba(212,175,55,0.1), rgba(212,175,55,0.05))', borderRadius: '8px', border: '2px solid var(--mystic-gold)' }}>
                      <span style={{ fontWeight: 'bold', color: 'var(--mystic-purple)', fontSize: '1.1rem' }}>Current Balance:</span>
                      <span style={{ color: 'var(--mystic-gold)', fontSize: '1.5rem', fontWeight: 'bold' }}>${searchResult.balance.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontWeight: '600', color: '#666' }}>Recipient:</span>
                      <span style={{ color: 'var(--mystic-purple)', fontWeight: '600', fontSize: '1.05rem' }}>{searchResult.recipient_name}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontWeight: '600', color: '#666' }}>Recipient Email:</span>
                      <span style={{ color: '#666', fontSize: '0.95rem' }}>{searchResult.recipient_email}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontWeight: '600', color: '#666' }}>From:</span>
                      <span style={{ color: 'var(--mystic-purple)', fontWeight: '600', fontSize: '1.05rem' }}>{searchResult.sender_name}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontWeight: '600', color: '#666' }}>Purchase Date:</span>
                      <span style={{ color: '#666' }}>{formatDate(searchResult.purchase_date)}</span>
                    </div>
                    {searchResult.services && (
                      <div style={{ padding: '1rem', background: 'rgba(139,92,246,0.05)', borderRadius: '8px', border: '2px solid rgba(139,92,246,0.3)' }}>
                        <span style={{ fontWeight: '600', color: 'var(--mystic-purple)', display: 'block', marginBottom: '0.5rem' }}>Included Services:</span>
                        <span style={{ color: '#666', fontSize: '0.95rem', whiteSpace: 'pre-line' }}>{searchResult.services}</span>
                      </div>
                    )}
                    {searchResult.message && (
                      <div style={{ padding: '1rem', background: 'rgba(212,175,55,0.05)', borderRadius: '8px', border: '2px solid rgba(212,175,55,0.3)' }}>
                        <span style={{ fontWeight: '600', color: 'var(--mystic-gold)', display: 'block', marginBottom: '0.5rem' }}>Personal Message:</span>
                        <span style={{ color: '#666', fontSize: '0.95rem', fontStyle: 'italic' }}>"{searchResult.message}"</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Redeem section for gift cards with balance */}
                  {searchResult.balance > 0 && (
                    <div style={{ padding: '1.5rem', background: 'white', borderRadius: '12px', border: '2px solid var(--mystic-purple)' }}>
                      <h4 style={{ marginBottom: '1rem', color: 'var(--mystic-purple)', fontSize: '1.1rem', fontWeight: 'bold' }}>Redeem Amount</h4>
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div><Label htmlFor="redeemAmount" style={{ fontWeight: '600', color: '#666' }}>Amount to Redeem</Label><Input id="redeemAmount" type="number" min="0" max={searchResult.balance} step="0.01" placeholder={`Max: $${searchResult.balance}`} value={redeemAmount} onChange={(e) => setRedeemAmount(e.target.value)} style={{ fontSize: '1rem', padding: '0.75rem' }} /></div>
                        <div><Label htmlFor="redeemService" style={{ fontWeight: '600', color: '#666' }}>Service (Optional)</Label><Input id="redeemService" placeholder="e.g., Balayage, Haircut" value={redeemService} onChange={(e) => setRedeemService(e.target.value)} style={{ fontSize: '1rem', padding: '0.75rem' }} /></div>
                        <Button onClick={handleRedeem} disabled={isRedeeming || !redeemAmount} style={{ padding: '0.875rem', fontSize: '1.1rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}>
                          {isRedeeming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Redeem</>}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Mark as Used section for service-only gift cards (balance = 0) */}
                  {searchResult.balance === 0 && searchResult.status === 'active' && (
                    <div style={{ padding: '1.5rem', background: 'white', borderRadius: '12px', border: '2px solid var(--mystic-gold)' }}>
                      <h4 style={{ marginBottom: '1rem', color: 'var(--mystic-purple)', fontSize: '1.1rem', fontWeight: 'bold' }}>Service Gift Card</h4>
                      <p style={{ color: '#666', marginBottom: '1rem' }}>
                        This is a service-only gift card. Click below to mark it as used after the services have been provided.
                      </p>
                      <Button onClick={handleMarkAsUsed} disabled={isRedeeming} style={{ width: '100%', padding: '0.875rem', fontSize: '1.1rem', fontWeight: 'bold', background: 'linear-gradient(135deg, var(--mystic-gold), #b8860b)', color: '#1a1a1a' }}>
                        {isRedeeming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Mark as Used</>}
                      </Button>
                    </div>
                  )}
                  
                  {/* Show message if service card is already used */}
                  {searchResult.balance === 0 && searchResult.status !== 'active' && (
                    <div style={{ padding: '1.5rem', background: 'rgba(239,68,68,0.1)', borderRadius: '12px', border: '2px solid rgba(239,68,68,0.3)', textAlign: 'center' }}>
                      <XCircle style={{ width: '48px', height: '48px', color: '#dc2626', margin: '0 auto 1rem' }} />
                      <h4 style={{ color: '#dc2626', fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Service Already Redeemed</h4>
                      <p style={{ color: '#666', fontSize: '0.95rem' }}>This service gift card has already been used.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transactions */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Calendar style={{ color: 'var(--mystic-gold)' }} /> Recent Transactions
            </h2>
            <Button onClick={() => setShowFilters(!showFilters)} style={{ background: showFilters ? 'var(--mystic-gold)' : 'rgba(255,255,255,0.1)', color: showFilters ? '#1a1a1a' : 'white', border: '1px solid var(--mystic-gold)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Filter className="w-4 h-4" /> Filters
            </Button>
          </div>
          {showFilters && (
            <Card style={{ marginBottom: '1rem', background: 'rgba(255,255,255,0.98)', border: '2px solid var(--mystic-gold)' }}>
              <CardContent style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div><Label style={{ fontWeight: '600', color: 'var(--mystic-purple)' }}>Status</Label><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '2px solid #e5e7eb' }}><option value="all">All Types</option><option value="purchase">Purchases</option><option value="redemption">Redemptions</option></select></div>
                <div><Label style={{ fontWeight: '600', color: 'var(--mystic-purple)' }}>Date Range</Label><select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '2px solid #e5e7eb' }}><option value="all">All Time</option><option value="today">Today</option><option value="week">Last 7 Days</option></select></div>
              </CardContent>
            </Card>
          )}
          <Card style={{ background: 'rgba(255,255,255,0.98)', borderRadius: '16px', border: '2px solid var(--mystic-gold)', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
            <CardContent style={{ padding: '2rem' }}>
              {filteredTransactions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}><Gift className="w-16 h-16" style={{ margin: '0 auto 1rem', opacity: 0.3 }} /><p>No transactions found</p></div>
              ) : (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {filteredTransactions.map((t, i) => (
                    <div key={i} style={{ padding: '1rem', background: 'linear-gradient(135deg, rgba(139,92,246,0.03), rgba(212,175,55,0.03))', borderRadius: '12px', border: '1px solid rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: t.type === 'purchase' ? 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.1))' : 'linear-gradient(135deg, rgba(59,130,246,0.2), rgba(59,130,246,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {t.type === 'purchase' ? <Gift className="w-6 h-6" style={{ color: '#10b981' }} /> : <DollarSign className="w-6 h-6" style={{ color: '#3b82f6' }} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: '600', color: 'var(--mystic-purple)' }}>{t.code}</span>
                          <span style={{ padding: '0.25rem 0.75rem', background: t.type === 'purchase' ? 'rgba(16,185,129,0.1)' : 'rgba(59,130,246,0.1)', color: t.type === 'purchase' ? '#059669' : '#2563eb', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>{t.type}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.9rem', color: '#666' }}>{t.description}</span>
                          <span style={{ fontSize: '0.85rem', color: '#999' }}>{formatDate(t.date)}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--mystic-gold)', marginRight: '0.5rem' }}>${t.amount.toFixed(2)}</div>
                      
                      {/* Download PDF Button */}
                      <Button
                        onClick={() => handleDownloadPdf(t.code)}
                        style={{
                          padding: '0.5rem',
                          background: 'linear-gradient(135deg, var(--mystic-gold), #b8860b)',
                          border: '2px solid var(--mystic-gold)',
                          color: '#1a1a1a',
                          minWidth: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                        title={`Download PDF for ${t.code}`}
                      >
                        <FileDown className="w-5 h-5" />
                      </Button>
                      
                      {/* Delete Button */}
                      <Button
                        onClick={() => handleDeleteSingleCard(t.code)}
                        disabled={deletingCardCode === t.code}
                        style={{
                          padding: '0.5rem',
                          background: deletingCardCode === t.code ? 'rgba(239,68,68,0.3)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                          border: '2px solid #ef4444',
                          color: 'white',
                          minWidth: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          cursor: deletingCardCode === t.code ? 'not-allowed' : 'pointer'
                        }}
                        title={`Delete gift card ${t.code}`}
                      >
                        {deletingCardCode === t.code ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GiftCardAdmin;
